import { ReactElement } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useSearchParams,
} from "react-router-dom";
import { EditProfile, Page404 } from "../index";
import { useGetFeatureFlag } from "lib/launch-darkly";
import { CompensationHistory } from "./compensation-history";
import { subroutes } from "pages/employer-of-record/account-settings/EorEmployeeAccountSettings";
import {
  useGetMemberTypeQuery,
  useGetEoRMemberByUuidQuery,
} from "types/generated/operations";
import { Spinner } from "@justworkshr/milo-core";
import BasicInfo from "./basic-info/basic-info";

//This is where the subroutes for account settings begin
const EDIT_PROFILE_PATH = "edit-profile";
const BASIC_INFO_PATH = "basic-information";
const COMPENSATION_HISTORY_PATH = "compensation-history";

// Determine which component to use for the /basic-information page based on the member type.
function getBasicInfoComponent(isEor: boolean) {
  if (isEor) {
    return {
      component: Outlet,
      subroutes: subroutes({ editable: true }),
    };
  }

  return {
    component: BasicInfo,
  };
}

export default function AccountSettings(): ReactElement {
  const { getFeatureFlag } = useGetFeatureFlag();
  const { data, loading } = useGetMemberTypeQuery();
  const [searchParams] = useSearchParams();

  const memberTypeIsEor =
    !!data?.authenticatedMember.memberType?.includes("EOR");

  // Dual-membership fallback: if the PEO member type is not EOR, check whether
  // the user has an EOR membership via ieor. This handles users who have a
  // terminated PEO record but an active EOR record (CSOBT-5901).
  const { data: eorData, loading: eorLoading } = useGetEoRMemberByUuidQuery({
    skip: loading || memberTypeIsEor,
  });

  const isEor = memberTypeIsEor || !!eorData?.eorMember;

  const basicInfo = getBasicInfoComponent(isEor);

  const BasicInfoComponent = basicInfo?.component;

  if (loading || eorLoading) return <Spinner />;

  return (
    <Routes>
      {!!BasicInfoComponent && (
        // If someone navigates to `/account-settings`, redirect to the basic information subsection
        <Route
          path=""
          element={
            <Navigate
              replace
              to={`${BASIC_INFO_PATH}?${searchParams.toString()}`}
            />
          }
        />
      )}
      <Route path={EDIT_PROFILE_PATH} element={<EditProfile />} />
      {!!BasicInfoComponent && (
        <Route path={BASIC_INFO_PATH} element={<BasicInfoComponent />}>
          {basicInfo?.subroutes}
        </Route>
      )}
      {getFeatureFlag("release-compensation-history-page-cf") && (
        <Route
          path={COMPENSATION_HISTORY_PATH}
          element={<CompensationHistory />}
        />
      )}
      <Route path="*" element={<Page404 />} />
    </Routes>
  );
}
